import { useEffect, useSyncExternalStore } from 'react';
import type { AsyncResource } from '../../../shared/useAsyncResource';

type Listener = () => void;

export type WorkspaceStateStore<T> = {
  getSnapshot: () => T;
  subscribe: (listener: Listener) => () => void;
  update: (update: (current: T) => T) => void;
};

export function createWorkspaceStateStore<T>(initial: T): WorkspaceStateStore<T> {
  let snapshot = initial;
  const listeners = new Set<Listener>();
  return {
    getSnapshot: () => snapshot,
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    update(update) {
      snapshot = update(snapshot);
      listeners.forEach((listener) => listener());
    },
  };
}

export type LazyResourceStore<T> = {
  getSnapshot: () => AsyncResource<T>;
  subscribe: (listener: Listener) => () => void;
  ensure: (load: () => Promise<T>) => void;
};

export function createLazyResourceStore<T>(): LazyResourceStore<T> {
  const listeners = new Set<Listener>();
  let loader: (() => Promise<T>) | undefined;
  let started = false;
  let generation = 0;
  const reload = () => {
    if (!loader) return;
    const activeGeneration = ++generation;
    snapshot = { data: undefined, loading: true, error: undefined, reload };
    listeners.forEach((listener) => listener());
    void Promise.resolve()
      .then(loader)
      .then(
        (data) => {
          if (activeGeneration !== generation) return;
          snapshot = { data, loading: false, error: undefined, reload };
          listeners.forEach((listener) => listener());
        },
        (reason: unknown) => {
          if (activeGeneration !== generation) return;
          snapshot = {
            data: undefined,
            loading: false,
            error: reason instanceof Error ? reason : new Error('데이터를 불러오지 못했습니다.'),
            reload,
          };
          listeners.forEach((listener) => listener());
        },
      );
  };
  let snapshot: AsyncResource<T> = { data: undefined, loading: true, error: undefined, reload };
  return {
    getSnapshot: () => snapshot,
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    ensure(load) {
      loader = load;
      if (started) return;
      started = true;
      reload();
    },
  };
}

export function useWorkspaceState<T>(store: WorkspaceStateStore<T>) {
  return useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);
}

export function useLazyResource<T>(
  store: LazyResourceStore<T>,
  load: () => Promise<T>,
  dependencies: readonly unknown[],
) {
  useEffect(() => store.ensure(load), [store, ...dependencies]);
  return useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);
}
